import PropTypes from "prop-types"
import React, { useState } from "react"
import { useMutation } from "react-query"

import updateCruiseStopOrder from "src/api/CruisePlan/updateCruiseStopOrder"

import { useToast } from "src/hooks/use_toast"

import CruisePlanEmptyState from "./CruisePlanEmptyState"
import CruisePlanMapModal from "./CruisePlanMapModal"
import CruisePlanMobile from "./CruisePlanMobile"
import CruisePlanTable from "./CruisePlanTable"
import displayDuration from "./displayDuration"

const CruisePlan = ({ cruisePlan }) => {
  const [stops, setStops] = useState(cruisePlan.cruiseStops)
  const [isMapModalOpen, setIsMapModalOpen] = useState(false)
  const { showToast } = useToast()

  const { mutate } = useMutation({
    mutationFn: (data) => updateCruiseStopOrder(cruisePlan.id, data),
    onError: () => {
      showToast("Error updating sort order", { type: "error" })
    },
  })

  const totalDuration = stops.reduce((acc, stop) => {
    return acc + (parseFloat(stop.duration) || 0)
  }, 0)

  const totalNauticalMiles = stops.reduce((acc, stop) => {
    return acc + (parseFloat(stop.nauticalMiles) || 0)
  }, 0)

  const totalStops = stops.length

  const handleMoveStop = (id, direction) => {
    setStops((stops) => {
      // find the stop to move and its index
      const index = stops.findIndex((stop) => stop.id === id)
      if (index === -1) return stops
      const stop = stops[index]

      // if the stop is at the top and we're moving up, or at the bottom and moving down, do nothing
      if (direction === "up" && stop.display_order === 0) return stops
      if (direction === "down" && stop.display_order === stops.length - 1)
        return stops

      // find the stop to swap with
      const swapIndex = index + (direction === "up" ? -1 : 1)
      if (swapIndex < 0 || swapIndex >= stops.length) return stops
      const swapStop = stops[swapIndex]
      if (!swapStop) return stops

      // swap the stops
      const newStops = [...stops]
      const [movedStop] = newStops.splice(index, 1)
      newStops.splice(swapIndex, 0, movedStop)

      // update the stop order
      const stopOrder = newStops.map((stop, index) => ({
        id: stop.id,
        display_order: index,
      }))
      mutate(stopOrder)
      return newStops
    })
  }

  const renderAddToPlanButton = () => {
    return (
      <a
        href={`/account/cruise_plans/${cruisePlan.id}/cruise_stops`}
        className="btn btn-secondary"
      >
        <i className="fa-kit fa-sf-plus mr-2" />
        Add to Plan
      </a>
    )
  }

  const renderStops = () => {
    return (
      <>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2">
            {renderAddToPlanButton()}
            {stops.length > 0 && (
              <button
                onClick={() => setIsMapModalOpen(true)}
                className="btn btn-secondary"
              >
                <i className="fa-kit fa-filled-location-pin mr-2" />
                View on Map
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {totalStops > 0 && (
              <div className="flex flex-col items-center rounded-lg border p-2 text-gray-900">
                <span className="font-bold">{totalStops}</span>
                <span className="text-gray-500">Stops</span>
              </div>
            )}
            {totalNauticalMiles > 0 && (
              <div className="flex flex-col items-center rounded-lg border p-2 text-gray-900">
                <span className="font-bold">{totalNauticalMiles} nm</span>
                <span className="text-gray-500">Trip Distance</span>
              </div>
            )}
            {totalDuration > 0 && (
              <div className="flex flex-col items-center rounded-lg border p-2 text-gray-900">
                <span className="font-bold">
                  {displayDuration(totalDuration)}
                </span>
                <span className="text-gray-500">Trip Duration</span>
              </div>
            )}
          </div>
        </div>
        <CruisePlanMobile
          stops={stops}
          cruisePlanId={cruisePlan.id}
          handleMoveStop={handleMoveStop}
        />
        <div className="hidden lg:block">
          <CruisePlanTable
            stops={stops}
            cruisePlanId={cruisePlan.id}
            handleMoveStop={handleMoveStop}
          />
        </div>
        <CruisePlanMapModal
          stops={stops}
          cruisePlanId={cruisePlan.id}
          cruisePlanName={cruisePlan.name}
          isOpen={isMapModalOpen}
          onClose={() => setIsMapModalOpen(false)}
        />
      </>
    )
  }

  return (
    <div className="container-fluid mb-8">
      <div className="flex items-center justify-between">
        <a href="/account/cruise_plans" className="text-link">
          ← Cruise plans
        </a>
        <a
          href={`/account/cruise_plans/${cruisePlan.id}/edit`}
          className="btn btn-secondary"
        >
          Edit plan details
        </a>
      </div>

      <div className="flex gap-4">
        <img
          className="cover size-24 rounded object-cover"
          src={cruisePlan.coverImage}
          alt={cruisePlan.name}
        />
        <div className="flex flex-col">
          <div className="text-2xl font-bold">{cruisePlan.name}</div>
          <div className="text-gray-500">{cruisePlan.description}</div>
        </div>
      </div>

      <hr className="my-4" />
      {stops.length > 0 ? (
        renderStops()
      ) : (
        <CruisePlanEmptyState
          title="No stops yet"
          description="Add your first stop."
          button={renderAddToPlanButton()}
        />
      )}
    </div>
  )
}

CruisePlan.propTypes = {
  cruisePlan: PropTypes.shape({
    id: PropTypes.string.isRequired,
    cruiseStops: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      })
    ).isRequired,
    coverImage: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
}

export default CruisePlan
